import React, { useEffect } from 'react';
import { ButtonGroup, ButtonGroupTypes } from 'devextreme-react/button-group';
import { SingleMultipleOrNone } from 'devextreme/ui/button_group';
import { Validator, RequiredRule } from 'devextreme-react/validator';
import { ValidatedEvent } from 'devextreme/ui/validator';
import { useState } from 'react';
import { InfoIconButton } from '../InfoIconButton';

export enum ToggleButtonLabelSize {
    None,
    Small,
    SmallBold,
    Medium,
    Large,
}

export type ToggleButtonItem = {
    id: string;
    icon?: string;
    text: string;
    alignment?: string;
    hint?: string;
    disabled?: boolean;
};

export interface IToggleButtonOptions {
    id: string;
    items: ToggleButtonItem[];
    selectedItems?: string[];
    selectionMode: SingleMultipleOrNone | undefined;
    stylingMode: ButtonGroupTypes.ButtonStyle;
    onItemClick?(e: ButtonGroupTypes.ItemClickEvent): void;
    width?: string;
    height?: string;
    required?: boolean;
    requiredMessage?: string;
    validationGroup?: string;
    helperText?: string;
    label?: any;
    labelSize?: ToggleButtonLabelSize;
    showInfoIcon?: boolean;
    infoText?: string;
}

export const ToggleButton: React.FC<IToggleButtonOptions> = ({
    id,
    width = 'auto',
    height = 'auto',
    items,
    selectedItems,
    selectionMode,
    stylingMode,
    onItemClick,
    required = false,
    requiredMessage = 'This field is required.',
    helperText,
    label,
    labelSize = ToggleButtonLabelSize.None,
    showInfoIcon = false,
    infoText,
}) => {
    const [changeTriggered, set_changeTriggered] = useState(false);
    const [errorMessage, set_errorMessage] = useState('');
    const callbacks: any[] = [];

    const onValidated = (validatedInfo: ValidatedEvent) => {
        set_errorMessage(validatedInfo?.brokenRule?.message ?? '');
    };

    const adapterConfig = {
        getValue: () => {
            return selectedItems && selectedItems.length >= 1 && selectedItems[0] != '';
        },
        applyValidationResults: onValidated,
        validationRequestsCallbacks: callbacks,
    };

    useEffect(() => {
        if (changeTriggered) {
            if (callbacks && callbacks.length > 0) {
                callbacks.forEach((func) => {
                    func();
                });
            }
        }
    }, [selectedItems]);

    const handleItemClick = (e) => {
        if (onItemClick) {
            onItemClick(e);
        }
        set_changeTriggered(true);
    };

    const getLabelClassName = () => {
        switch (labelSize) {
            case ToggleButtonLabelSize.Small:
                return 'togglebutton-labelSmall';
            case ToggleButtonLabelSize.SmallBold:
                return 'togglebutton-labelSmallBold';
            case ToggleButtonLabelSize.Medium:
                return 'togglebutton-labelMedium';
            case ToggleButtonLabelSize.Large:
                return 'togglebutton-labelLarge';
            case ToggleButtonLabelSize.None:
                return;
            default:
                return;
        }
    };

    const getControlSpacing = () => {
        switch (labelSize) {
            case ToggleButtonLabelSize.Small:
            case ToggleButtonLabelSize.SmallBold:
                return '5px';
            case ToggleButtonLabelSize.Medium:
                return '10px';
            case ToggleButtonLabelSize.Large:
                return '20px';
            case ToggleButtonLabelSize.None:
                return;
            default:
                return;
        }
    };

    return (
        <>
            {label && (
                <div className={getLabelClassName()}>
                    {label}
                    {required ? ' *' : ''}
                    {showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}
            <div
                className={`togglebuttongroup-container ${errorMessage ? 'error' : ''}`}
                style={{ marginTop: getControlSpacing() }}
            >
                <ButtonGroup
                    id={id}
                    items={items}
                    keyExpr='id'
                    selectionMode={selectionMode}
                    stylingMode={stylingMode}
                    selectedItemKeys={selectedItems}
                    onItemClick={handleItemClick}
                    width={width}
                    height={height}
                    className={`togglebuttongroup-input ${errorMessage ? 'error' : ''}`}
                />
                <Validator adapter={adapterConfig}>{required && <RequiredRule message={requiredMessage} />}</Validator>
                {/* Show helper text only if there is no error */}
                {errorMessage ? (
                    <div className='togglebuttongroup-error'>{errorMessage}</div>
                ) : (
                    helperText && <div className='togglebuttongroup-helper'>{helperText}</div>
                )}
            </div>
        </>
    );
};
